/**
 * LiveBars (BL-56b) — the two live Pro-Bars, wired at last.
 *
 * BL-56 built the `ProBar` (the lit-CRT value-bar) and BL-56's demo
 * (`docs/hud_probar.html`) showed a wave-progress bar and a payload-charge bar,
 * but they were never wired to the running game. This mounts the REAL `ProBar`
 * for both, in the right-rail (the systems/armory column), reading live state
 * through O(1) render-only accessors:
 *
 *   - WAVE — waves cleared / total (the run-progress read; `WaveManager`);
 *   - PAYLOAD — the rack charge: armed payloads / capacity (`PayloadRack`).
 *
 * Determinism-neutral: both accessors are pure reads (the sim never sees these),
 * refreshed only on the relevant already-emitted events (no per-frame poll). The
 * `ProBar`'s own value-flash is reduced-motion-gated. Colour is central
 * (`COLORS.ICE_CYAN` + the legendary gold) — no new gameplay hex.
 */
import type { EventBus } from "../core/EventBus.ts";
import { COLORS } from "../core/constants.ts";
import { RARITY } from "../core/palette.ts";
import { ProBar } from "./ProBar.ts";
import { UI_FONT_STACK, UI_SPACE, roleFontSize } from "./uiTokens.ts";

/** Live, O(1), render-only state providers (wired from `main.ts`). */
export interface LiveBarsSources {
  /** Waves cleared / total — the strategic run-progress read. */
  waveProgress: () => { cleared: number; total: number };
  /** Payload-rack charge — armed count / capacity. */
  payloadCharge: () => { count: number; max: number };
}

export class LiveBars {
  readonly el: HTMLDivElement;
  private readonly waveBar: ProBar;
  private readonly payloadBar: ProBar;
  private readonly sources: LiveBarsSources;
  private readonly unsubs: (() => void)[] = [];

  constructor(parent: HTMLElement, sources: LiveBarsSources, bus: EventBus) {
    this.sources = sources;
    LiveBars.ensureStyles();

    this.el = document.createElement("div");
    this.el.id = "live-bars";

    const head = document.createElement("div");
    head.className = "lb-head";
    head.textContent = "// SIGNAL";
    this.el.appendChild(head);

    const wave = sources.waveProgress();
    this.waveBar = new ProBar({
      color: COLORS.ICE_CYAN,
      max: Math.max(1, wave.total),
      value: wave.cleared,
      // Bug 10: this bar is run-completion progress (waves CLEARED / total), not
      // the current-wave index shown in the top HUD — label it distinctly.
      label: "PROGRESS",
      valueText: () => {
        const w = this.sources.waveProgress();
        return `${w.cleared}/${w.total}`;
      },
      extraClasses: ["live-bar"],
    });
    this.el.appendChild(this.waveBar.el);

    const pay = sources.payloadCharge();
    this.payloadBar = new ProBar({
      color: RARITY.legendary.mid,
      max: Math.max(1, pay.max),
      value: pay.count,
      state: pay.count > 0 ? "active" : "idle",
      label: "PAYLOAD",
      valueText: () => {
        const p = this.sources.payloadCharge();
        return `${p.count}/${p.max}`;
      },
      extraClasses: ["live-bar"],
    });
    this.el.appendChild(this.payloadBar.el);

    parent.appendChild(this.el);
    this.waveBar.mount();
    this.payloadBar.mount();

    // Refresh only on the relevant already-emitted events — never per-frame.
    // (A run reset re-refreshes via `resetForNewRun` → `liveBars.refresh()`.)
    const refresh = (): void => this.refresh();
    this.unsubs.push(
      bus.on("wave.started", refresh),
      bus.on("wave.completed", refresh),
      bus.on("payload.armed", refresh),
      bus.on("payload.fired", refresh),
    );
    this.refresh();
  }

  /** Re-read the live accessors and push them into the bars (render-only). */
  refresh(): void {
    const w = this.sources.waveProgress();
    this.waveBar.setMax(Math.max(1, w.total));
    this.waveBar.setValue(w.cleared);

    const p = this.sources.payloadCharge();
    this.payloadBar.setMax(Math.max(1, p.max));
    this.payloadBar.setValue(p.count);
    this.payloadBar.setState(p.count > 0 ? "active" : "idle");
  }

  destroy(): void {
    for (const unsub of this.unsubs) unsub();
    this.unsubs.length = 0;
    this.waveBar.destroy();
    this.payloadBar.destroy();
    this.el.remove();
  }

  // ─── One injected stylesheet for the section ──────────────────────────────

  private static stylesInjected = false;

  static ensureStyles(): void {
    if (LiveBars.stylesInjected || typeof document === "undefined") return;
    if (document.getElementById("live-bars-styles")) {
      LiveBars.stylesInjected = true;
      return;
    }
    LiveBars.stylesInjected = true;
    const style = document.createElement("style");
    style.id = "live-bars-styles";
    style.textContent = `
      /* Right-rail-headroom correction (docs/decisions.md D-335, amended) —
         "// SIGNAL" is the least load-bearing block in the rail: tightened to
         the hair(2px)/xxs(4px) ladder steps (padding 7/9->2/4, gap 6->4px),
         the same premium-neutral tightening D-333/D-335 already used
         elsewhere on this rail. Fixed design-px, so the saving is identical
         at every --chrome-scale. */
      #live-bars {
        display: flex; flex-direction: column; gap: ${UI_SPACE.xxs}px;
        padding: ${UI_SPACE.hair}px ${UI_SPACE.sm}px ${UI_SPACE.xxs}px;
        border-top: 1px solid #102a16;
        font-family: ${UI_FONT_STACK};
      }
      #live-bars .lb-head {
        font-size: ${roleFontSize("micro")};
        color: #6f8a72; letter-spacing: 1.8px; text-transform: uppercase;
        line-height: 1.1;
      }
      /* Each bar needs an explicit box for the lit canvas to paint into.
         Right-rail-headroom correction (docs/decisions.md D-335, amended):
         ProBar.ts:613's value text is chrome-scale-driven (numeric role, up
         to ~1.875x at the phone-tier ceiling) while this track was a FIXED
         16px (perf/layout reasons) — the label/value already overhung the
         box even at the owner's real device scale. Two changes, scoped to
         these two bars only (LiveBars is the sole .live-bar consumer of the
         shared ProBar): (1) the value drops from the larger "numeric" role
         to the label's own "micro" role — no reason for it to be the bigger
         of the two in a compact rail bar; (2) the track grows 16->21px (a
         small, budget-neutral cost against the >26px margin this correction
         banks at every shipped scale) so the "micro" role's own scaled line
         box clears it cleanly at the owner's real device scale, not just
         nominally smaller than before. */
      #live-bars .live-bar { width: 100%; height: 21px; }
      #live-bars .live-bar .probar-value {
        font-size: ${roleFontSize("micro")};
      }
      #live-bars .live-bar .probar-label,
      #live-bars .live-bar .probar-value {
        line-height: 1;
      }
    `;
    document.head.appendChild(style);
  }
}

/** Test/reset hook — drops the injected-style guard. */
export function __resetLiveBarsForTest(): void {
  LiveBars["stylesInjected"] = false;
}
